import { useEffect, useState, useRef } from "react";

/**
 * Custom hook to handle scroll-based visibility for mobile components
 * Hides component when scrolling down, shows when scrolling up or when scroll stops
 *
 * @param {Object} options - Configuration options
 * @param {number} options.scrollThreshold - Minimum scroll distance to trigger hide/show (default: 0)
 * @param {number} options.hideDelay - Delay in ms before hiding on scroll down (default: 0)
 * @param {number} options.showDelay - Delay in ms before showing when scroll stops (default: 0)
 * @param {boolean} options.enabled - Whether the hook is enabled (default: true)
 * @returns {boolean} - Whether the component should be visible
 */
export const useScrollVisibility = ({
  scrollThreshold = 0,
  hideDelay = 0,
  showDelay = 150, // Default to 150ms to properly detect scroll stop
  enabled = true,
} = {}) => {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef(null);
  const hideTimeout = useRef(null);
  const isScrollingDown = useRef(false);
  const lastScrollDirection = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Reads the current vertical scroll offset from `window` or `document`.
   *
   * @returns {number} Scroll position in pixels, or 0 if unavailable.
   */
  const getScrollY = () => {
    try {
      if (typeof window === "undefined") return 0;
      const scrollY = window.scrollY || window.pageYOffset;
      if (typeof scrollY === "number" && !isNaN(scrollY)) {
        return scrollY;
      }
      if (typeof document !== "undefined" && document.documentElement) {
        const docScrollTop = document.documentElement.scrollTop;
        if (typeof docScrollTop === "number" && !isNaN(docScrollTop)) {
          return docScrollTop;
        }
      }
      return 0;
    } catch (error) {
      console.error("Error getting scroll position:", error);
      return 0;
    }
  };

  /**
   * Sets visibility only while the hook instance is still mounted.
   *
   * @param {boolean} value - Next visibility flag.
   * @returns {void}
   */
  const safeSetIsVisible = (value) => {
    if (isMountedRef.current) {
      try {
        setIsVisible(value);
      } catch (error) {
        console.error("Error setting visibility state:", error);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled || typeof window === "undefined") {
      return;
    }

    // Validate delay values
    const safeHideDelay = Math.max(0, Number(hideDelay) || 0);
    const safeShowDelay = Math.max(0, Number(showDelay) || 150);
    const safeScrollThreshold = Math.max(0, Number(scrollThreshold) || 0);

    // Initialize lastScrollY to current scroll position
    lastScrollY.current = getScrollY();

    /**
     * Handles the scroll event and updates the visibility state based on scroll direction and threshold.
     *
     * @returns {void}
     */
    const handleScroll = () => {
      try {
        if (!isMountedRef.current) return;

        const currentScrollY = getScrollY();

        // Validate scroll position
        if (typeof currentScrollY !== "number" || isNaN(currentScrollY)) {
          return;
        }

        // Clear any existing timeouts
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
          scrollTimeout.current = null;
        }
        if (hideTimeout.current) {
          clearTimeout(hideTimeout.current);
          hideTimeout.current = null;
        }

        const scrollDifference = Math.abs(currentScrollY - lastScrollY.current);

        // Only process if scroll difference is significant
        if (scrollDifference < safeScrollThreshold) {
          lastScrollY.current = currentScrollY;
          return;
        }

        // Determine scroll direction
        const scrollingDown = currentScrollY > lastScrollY.current;
        isScrollingDown.current = scrollingDown;
        lastScrollDirection.current = scrollingDown ? "down" : "up";

        if (scrollingDown) {
          // Scrolling down - hide after delay
          hideTimeout.current = setTimeout(() => {
            if (isMountedRef.current) {
              safeSetIsVisible(false);
            }
          }, safeHideDelay);
          // Set timeout to show when scroll stops - only fires if no new scroll events come
          // Capture the scroll position at this moment
          const scrollYAtTimeout = currentScrollY;
          scrollTimeout.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            try {
              // Re-check scroll position to see if scroll has stopped
              const finalScrollY = getScrollY();
              // Only show if scroll position hasn't changed (scroll has stopped)
              if (typeof finalScrollY === "number" && !isNaN(finalScrollY) && finalScrollY === scrollYAtTimeout) {
                safeSetIsVisible(true);
              }
            } catch (error) {
              console.error("Error in scroll timeout callback:", error);
            }
          }, safeShowDelay);
        } else {
          // Scrolling up - show immediately
          safeSetIsVisible(true);
          // Clear any pending hide timeout
          if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
          }
        }

        lastScrollY.current = currentScrollY;
      } catch (error) {
        console.error("Error in handleScroll:", error);
      }
    };

    // Add scroll event listener with passive option for better performance
    try {
      window.addEventListener("scroll", handleScroll, { passive: true });
    } catch (error) {
      console.error("Error adding scroll event listener:", error);
    }

    return () => {
      isMountedRef.current = false;
      try {
        window.removeEventListener("scroll", handleScroll);
      } catch (error) {
        console.error("Error removing scroll event listener:", error);
      }
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = null;
      }
      if (hideTimeout.current) {
        clearTimeout(hideTimeout.current);
        hideTimeout.current = null;
      }
    };
  }, [enabled, scrollThreshold, hideDelay, showDelay]);

  return isVisible;
};

export default useScrollVisibility;
